/**
 * A-03 / A-04 — doctor onboarding + lifecycle (Medicines-style layout).
 *
 * Add button reveals the DoctorForm. The list is a server-driven virtual-scroll table
 * (components/ui/VirtualTable: only its rows scroll, so the filters above stay in view) with
 * search, status / verified / login / added-by filters, sort, and per-page batch size. An
 * unverified doctor's row — pending, or active because a pharmacist added them while digitising —
 * expands an inline checklist + verify panel (ADM-04). Lifecycle actions are icon buttons.
 * Reset password sets a password the admin passes on, for the doctor to change after signing in
 * (not offered while a doctor is pending: activating sends them a sign-up link instead).
 *
 * Login access: each row has a login switch (turning it off asks first), and rows can be
 * multi-selected (the header box selects the loaded rows, then offers every doctor matching the
 * filters) to turn login on or off in bulk. Doctors a pharmacist added while digitising arrive
 * with login off.
 */

import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  activateDoctor,
  createDoctor,
  DOCTOR_CHECKLIST_ITEMS,
  listDoctors,
  reactivateDoctor,
  resetDoctorPassword,
  setDoctorChecklist,
  setDoctorLoginAccess,
  suspendDoctor,
  updateDoctor,
} from "../../api/admin";
import type { Doctor, DoctorListFilter, DoctorLoginAccessResult, DoctorQuery, SignupLink } from "../../api/admin";
import { ApiError, describeError } from "../../api/errors";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { ResetPasswordForm } from "../../components/ResetPasswordForm";
import { ExportButton } from "../../components/ui/ExportButton";
import { FilterBar } from "../../components/ui/FilterBar";
import {
  CheckIcon,
  ChecklistIcon,
  EditIcon,
  IconButton,
  KeyIcon,
  PauseIcon,
  PlayIcon,
  RowActions,
  SaveIcon,
} from "../../components/ui/IconButton";
import { Modal } from "../../components/ui/Modal";
import { PrefixInput } from "../../components/ui/PrefixInput";
import { Select } from "../../components/ui/Select";
import { TableRow, VirtualTable } from "../../components/ui/VirtualTable";
import { signupLinkSent, toastFailure } from "../../lib/adminFeedback";
import type { TableColumn } from "../../lib/tableColumns";
import {
  byPageNumber,
  EXPORT_PAGE_SIZE,
  sheetDateTime,
  sheetLabel,
  sheetPhone,
  type ExportColumn,
} from "../../lib/tableExport";
import { DoctorForm } from "./DoctorForm";
import type { DoctorFormValues } from "./DoctorForm";

const PER_PAGE_OPTS = [25, 50, 100];
const SORTS = [
  { key: "name", label: "Name" },
  { key: "created_at", label: "Created" },
];
const ROW_H = 60; // the estimate before a row is measured (rows wrap on a phone, and expand)
// Columns: select · Name · Council · Verified · Status · Login · Actions. An active, unverified
// doctor (ADM-04) has the most actions — Edit, Reset password, Checklist, Verify and Suspend: four
// to a line from sm, all five from xl; on a phone they wrap two by two. Council (from lg),
// Verified (md) and Status (sm) show under the name below their widths.
const COLUMNS: TableColumn[] = [
  { key: "select", header: null, width: "1rem" },
  { key: "name", header: "Name", width: { base: "minmax(0,1fr)", lg: "minmax(0,2.5fr)" } },
  { key: "council", header: "Council", width: "minmax(0,1.5fr)", from: "lg" },
  { key: "verified", header: "Verified", width: "5.5rem", from: "md" },
  { key: "status", header: "Status", width: "6rem", from: "sm" },
  { key: "login", header: "Login", width: "4rem" },
  { key: "actions", header: "Actions", width: { base: "4.25rem", sm: "9.25rem", xl: "11rem" }, align: "end" },
];
// POST /admin/doctors/login-access takes up to 1000 doctor_ids; "all matching" sends the filter.
const MAX_LOGIN_IDS = 1000;

// The licence number only ever leaves the API masked.
const EXPORT_COLUMNS: ExportColumn<Doctor>[] = [
  { header: "Name", value: (d) => d.full_name },
  { header: "Phone", value: (d) => sheetPhone(d.phone) },
  { header: "Email", value: (d) => d.email },
  { header: "Specialisation", value: (d) => d.specialisation },
  { header: "State medical council", value: (d) => d.state_medical_council },
  { header: "Licence no. (masked)", value: (d) => d.license_number_masked },
  { header: "Status", value: (d) => sheetLabel(d.status) },
  { header: "Verified on (IST)", value: (d) => sheetDateTime(d.verified_at) },
  { header: "Can sign in", value: (d) => d.login_enabled },
  { header: "Added by", value: (d) => [d.added_by_name, d.added_by_role && `(${d.added_by_role})`].filter(Boolean).join(" ") },
];

export function Doctors() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["doctors"] });

  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [signupLink, setSignupLink] = useState<SignupLink | null>(null);
  const [editDoctor, setEditDoctor] = useState<Doctor | null>(null);
  const [resetDoctor, setResetDoctor] = useState<Doctor | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const resetPassword = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => resetDoctorPassword(id, password),
    onSuccess: (d) => {
      setResetDoctor(null);
      invalidate();
      toast.success(`Password reset for ${d.full_name}. They’ll be asked to change it after signing in.`);
    },
    onError: (e) => setResetError(describeError(e)),
  });

  // list controls
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<DoctorQuery["status"]>("");
  const [verified, setVerified] = useState<"" | "true" | "false">("");
  const [login, setLogin] = useState<"" | "true" | "false">("");
  const [addedBy, setAddedBy] = useState<NonNullable<DoctorQuery["added_by"]>>("");
  const [sort, setSort] = useState("name");
  const [descending, setDescending] = useState(false);
  const [perPage, setPerPage] = useState(25);

  const query: DoctorQuery = {
    q,
    status,
    verified: verified === "" ? null : verified === "true",
    login_enabled: login === "" ? null : login === "true",
    added_by: addedBy,
    sort,
    descending,
    per_page: perPage,
  };

  const createKey = useIdempotencyKey();
  const create = useMutation({
    mutationFn: (v: DoctorFormValues) => {
      const body = {
        full_name: v.full_name,
        phone: v.phone,
        email: v.email || undefined,
        state_medical_council: v.state_medical_council,
        license_number: v.license_number,
        specialisation: v.specialisation || undefined,
        verification_checklist: v.verification_checklist,
      };
      return createDoctor(body, createKey.keyFor(body));
    },
    onSuccess: () => {
      createKey.reset();
      setFormOpen(false);
      setFormError(null);
      invalidate();
    },
    onError: (e) => setFormError(describeError(e)),
  });

  const narrowing = (status ? 1 : 0) + (verified ? 1 : 0) + (login ? 1 : 0) + (addedBy ? 1 : 0);
  const activeFilters = narrowing + (q ? 1 : 0);

  return (
    <div className="flex h-full flex-col gap-3 sm:gap-5">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Doctors</h2>
          <p className="text-sm text-gray-500">Onboard, verify, and manage prescribing doctors.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Every doctor the search and filters match, in the table's order. */}
          <ExportButton
            table="doctors"
            columns={EXPORT_COLUMNS}
            keyOf={(d) => d.id}
            fetchPage={byPageNumber((page) => listDoctors({ ...query, page, per_page: EXPORT_PAGE_SIZE }))}
          />
          <button
            onClick={() => {
              setFormError(null);
              setFormOpen(true);
            }}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg shadow-xs hover:opacity-90"
          >
            + Onboard doctor
          </button>
        </div>
      </div>

      <Modal open={formOpen} title="Onboard doctor" size="lg" onClose={() => setFormOpen(false)}>
        <DoctorForm
          submitting={create.isPending}
          error={formError}
          onCancel={() => setFormOpen(false)}
          onSubmit={(v) => create.mutate(v)}
        />
      </Modal>

      <Modal open={editDoctor !== null} title="Edit doctor" onClose={() => setEditDoctor(null)}>
        {editDoctor && (
          <EditDoctorForm
            doctor={editDoctor}
            onClose={() => setEditDoctor(null)}
            onSaved={() => {
              setEditDoctor(null);
              invalidate();
            }}
          />
        )}
      </Modal>

      <Modal open={resetDoctor !== null} title="Reset password" onClose={() => setResetDoctor(null)}>
        {resetDoctor && (
          <ResetPasswordForm
            key={resetDoctor.id}
            name={resetDoctor.full_name}
            note={
              !resetDoctor.login_enabled
                ? "Login is off for this doctor: turn it on in the Login column so they can sign in with this password."
                : resetDoctor.status === "suspended"
                  ? "This doctor is suspended: reactivate them so they can sign in with this password."
                  : undefined
            }
            submitting={resetPassword.isPending}
            error={resetError}
            onCancel={() => setResetDoctor(null)}
            onSubmit={(password) => resetPassword.mutate({ id: resetDoctor.id, password })}
          />
        )}
      </Modal>

      {signupLink?.signup_token_dev && (
        <div className="shrink-0 break-all rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
          Activated. Dev signup token: <code className="font-mono">{signupLink.signup_token_dev}</code>
        </div>
      )}

      <FilterBar
        label="Search and filter doctors"
        narrowing={narrowing}
        search={
          <Control label="Search" className="min-w-[180px] flex-1">
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="pointer-events-none absolute left-3 top-1/2 h-6 w-6 -translate-y-1/2 text-gray-500"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name or phone…"
                className="w-full rounded-lg border py-1.5 pl-11 pr-2 outline-hidden focus:border-brand focus:ring-1 focus:ring-brand"
              />
            </div>
          </Control>
        }
      >
        <Control label="Status">
          <Select
            value={status ?? ""}
            options={[
              { value: "", label: "All" },
              { value: "pending_verification", label: "Pending" },
              { value: "active", label: "Active" },
              { value: "suspended", label: "Suspended" },
            ]}
            onChange={setStatus}
          />
        </Control>
        <Control label="Verified">
          <Select
            value={verified}
            options={[
              { value: "", label: "All" },
              { value: "true", label: "Verified" },
              { value: "false", label: "Not verified" },
            ]}
            onChange={setVerified}
          />
        </Control>
        <Control label="Login">
          <Select
            value={login}
            options={[
              { value: "", label: "All" },
              { value: "true", label: "On" },
              { value: "false", label: "Off" },
            ]}
            onChange={setLogin}
            data-testid="doctor-filter-login"
          />
        </Control>
        <Control label="Added by">
          <Select
            value={addedBy}
            options={[
              { value: "", label: "Anyone" },
              { value: "admin", label: "Admin" },
              { value: "pharmacist", label: "Pharmacy" },
            ]}
            onChange={setAddedBy}
            data-testid="doctor-filter-added-by"
          />
        </Control>
        <Control label="Sort">
          <div className="flex">
            <Select value={sort} options={SORTS.map((s) => ({ value: s.key, label: s.label }))} onChange={setSort} className="flex-1 rounded-r-none" />
            <button onClick={() => setDescending((d) => !d)} className="select-addon" title="Toggle direction">
              {descending ? "↓" : "↑"}
            </button>
          </div>
        </Control>
        <Control label="Batch size">
          <Select value={perPage} options={PER_PAGE_OPTS.map((n) => ({ value: n, label: String(n) }))} onChange={setPerPage} />
        </Control>
        {activeFilters > 0 && (
          <button
            onClick={() => {
              setQ("");
              setStatus("");
              setVerified("");
              setLogin("");
              setAddedBy("");
            }}
            className="ml-auto self-end rounded-lg px-3 py-1.5 text-gray-500 hover:bg-gray-100"
          >
            Clear filters ({activeFilters})
          </button>
        )}
      </FilterBar>

      <DoctorList
        query={query}
        onActivated={setSignupLink}
        onEdit={setEditDoctor}
        onResetPassword={(d) => {
          setResetError(null);
          setResetDoctor(d);
        }}
      />
    </div>
  );
}

// ---------------- Virtual-scroll table ----------------

/** The list filters in the shape the bulk login endpoint takes ("all matching"). */
function toListFilter(query: DoctorQuery): DoctorListFilter {
  return {
    q: query.q?.trim() || null,
    status: query.status || null,
    verified: query.verified ?? null,
    login_enabled: query.login_enabled ?? null,
    added_by: query.added_by || null,
  };
}

function DoctorList({
  query,
  onActivated,
  onEdit,
  onResetPassword,
}: {
  query: DoctorQuery;
  onActivated: (l: SignupLink) => void;
  onEdit: (d: Doctor) => void;
  onResetPassword: (d: Doctor) => void;
}) {
  const qc = useQueryClient();
  const {
    data,
    error,
    isError,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery({
    queryKey: ["doctors", query],
    queryFn: ({ pageParam }) => listDoctors({ ...query, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.has_more ? last.page + 1 : undefined),
  });

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const total = data?.pages[0]?.total ?? 0;
  const [expanded, setExpanded] = useState<string | null>(null);

  // Selection: explicit ids, or every doctor matching the filters (incl. rows not loaded yet).
  // A new search / filter starts with nothing selected.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [allMatching, setAllMatching] = useState(false);
  const queryId = JSON.stringify(query);
  const [selectionFor, setSelectionFor] = useState(queryId);
  if (selectionFor !== queryId) {
    setSelectionFor(queryId);
    setSelected(new Set());
    setAllMatching(false);
  }
  const loadedIds = rows.map((d) => d.id);
  const allLoadedSelected = rows.length > 0 && (allMatching || loadedIds.every((id) => selected.has(id)));
  const selectedCount = allMatching ? total : selected.size;
  const tooMany = !allMatching && selected.size > MAX_LOGIN_IDS;
  const clearSelection = () => {
    setSelected(new Set());
    setAllMatching(false);
  };
  const toggleOne = (id: string) => {
    if (allMatching) {
      // Leaving "all matching": keep the loaded rows minus this one.
      setAllMatching(false);
      setSelected(new Set(loadedIds.filter((x) => x !== id)));
      return;
    }
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleLoaded = () => {
    if (allLoadedSelected) clearSelection();
    else setSelected(new Set(loadedIds));
  };

  const [confirm, setConfirm] = useState<boolean | null>(null); // the login value to confirm
  const bulk = useMutation({
    mutationFn: (on: boolean) =>
      setDoctorLoginAccess(
        allMatching
          ? { login_enabled: on, filter: toListFilter(query) }
          : { login_enabled: on, doctor_ids: [...selected] },
      ),
    onSuccess: (res) => {
      toast.success(loginResultMessage(res));
      setConfirm(null);
      clearSelection();
      void qc.invalidateQueries({ queryKey: ["doctors"] });
    },
    onError: toastFailure,
  });
  // Selected doctors who still can't sign in after login is on (status isn't active).
  const notActiveSelected = allMatching ? 0 : rows.filter((d) => selected.has(d.id) && d.status !== "active").length;

  const columns = COLUMNS.map((c) =>
    c.key === "select"
      ? {
          ...c,
          header: (
            <SelectBox
              checked={allLoadedSelected}
              indeterminate={!allLoadedSelected && selected.size > 0}
              disabled={rows.length === 0}
              onChange={toggleLoaded}
              label={allLoadedSelected ? "Clear selection" : "Select all loaded doctors"}
            />
          ),
        }
      : c,
  );

  // The bulk actions sit above the column header, in view however far the rows are scrolled.
  const offerAllMatching = allLoadedSelected && (total > rows.length || tooMany);
  const toolbar =
    selectedCount > 0 || offerAllMatching ? (
      <div className="shrink-0">
        {selectedCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b bg-brand/5 px-4 py-2 text-sm">
            <span className="font-medium text-gray-800">
              {selectedCount} selected{allMatching ? " (all matching)" : ""}
            </span>
            <span className="mx-1 h-4 w-px bg-gray-300" aria-hidden />
            <button
              type="button"
              onClick={() => setConfirm(true)}
              disabled={tooMany}
              className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              Turn login on
            </button>
            <button
              type="button"
              onClick={() => setConfirm(false)}
              disabled={tooMany}
              className="rounded-lg border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Turn login off
            </button>
            {tooMany && <span className="text-xs text-amber-900">Up to {MAX_LOGIN_IDS} at a time: select all matching instead.</span>}
            <button type="button" onClick={clearSelection} className="ml-auto text-xs text-gray-500 hover:underline">
              Clear selection
            </button>
          </div>
        )}
        {offerAllMatching && (
          <div className="border-b bg-amber-50 px-4 py-1.5 text-center text-xs text-amber-900">
            {allMatching ? (
              <>
                All {total} doctors matching the filters are selected.{" "}
                <button type="button" onClick={clearSelection} className="font-medium underline">
                  Clear selection
                </button>
              </>
            ) : (
              <>
                The {rows.length} loaded doctors are selected.{" "}
                <button type="button" onClick={() => setAllMatching(true)} className="font-medium underline">
                  Select all {total} matching doctors
                </button>
              </>
            )}
          </div>
        )}
      </div>
    ) : null;

  return (
    <>
      <VirtualTable
        label="Doctors"
        columns={columns}
        rows={rows}
        rowKey={(d) => d.id}
        rowHeight={ROW_H}
        measureRows
        loading={isLoading}
        error={isError ? describeError(error) : null}
        onRetry={() => void refetch()}
        empty="No doctors match your filters."
        toolbar={toolbar}
        total={data?.pages[0]?.total}
        hasMore={hasNextPage}
        loadingMore={isFetchingNextPage}
        loadMoreFailed={isFetchNextPageError}
        onLoadMore={fetchNextPage}
        resetKey={queryId}
        renderRow={(d) => (
          <DoctorRow
            doctor={d}
            selected={allMatching || selected.has(d.id)}
            onSelect={() => toggleOne(d.id)}
            expanded={expanded === d.id}
            onToggle={() => setExpanded((cur) => (cur === d.id ? null : d.id))}
            onExpand={() => setExpanded(d.id)}
            onActivated={onActivated}
            onEdit={onEdit}
            onResetPassword={onResetPassword}
          />
        )}
      />

      <ConfirmLogin
        on={confirm}
        who={`${selectedCount} doctor${selectedCount === 1 ? "" : "s"}`}
        notActive={notActiveSelected}
        busy={bulk.isPending}
        onConfirm={(on) => bulk.mutate(on)}
        onClose={() => setConfirm(null)}
      />
    </>
  );
}

function loginResultMessage(res: DoctorLoginAccessResult): string {
  const state = res.login_enabled ? "on" : "off";
  const msg = `Login turned ${state} for ${res.updated} doctor${res.updated === 1 ? "" : "s"}`;
  const unchanged = res.matched - res.updated;
  return unchanged > 0 ? `${msg} (${unchanged} already ${state}).` : `${msg}.`;
}

function DoctorRow({
  doctor,
  selected,
  onSelect,
  expanded,
  onToggle,
  onExpand,
  onActivated,
  onEdit,
  onResetPassword,
}: {
  doctor: Doctor;
  selected: boolean;
  onSelect: () => void;
  expanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onActivated: (l: SignupLink) => void;
  onEdit: (d: Doctor) => void;
  onResetPassword: (d: Doctor) => void;
}) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["doctors"] });
  const [checklist, setChecklist] = useState(doctor.verification_checklist || {});
  const [error, setError] = useState<string | null>(null);
  const isPending = doctor.status === "pending_verification";
  // Pending doctors, and active ones a pharmacist added while digitising, still need verifying (ADM-04).
  const canVerify = !doctor.verified_at && (isPending || doctor.status === "active");

  const saveChecklist = useMutation({
    mutationFn: () => setDoctorChecklist(doctor.id, checklist),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(describeError(e)),
  });
  // Green "saved" state only after the backend confirms; any later edit resets it (see checkbox onChange).
  const checklistSaved = saveChecklist.isSuccess;
  const activate = useMutation({
    mutationFn: () => activateDoctor(doctor.id),
    onSuccess: (link) => {
      onActivated(link);
      toast.success(signupLinkSent(link, `${doctor.full_name} is active. Sign-up link sent`));
      invalidate();
    },
    onError: (e) => {
      // The backend gates activation on a completed checklist (409 CHECKLIST_INCOMPLETE). Don't
      // fail silently: tell the admin what to do AND open the checklist panel so they can act —
      // the inline error otherwise lives in a panel that may be collapsed.
      if (e instanceof ApiError && e.code === "CHECKLIST_INCOMPLETE") {
        const msg = "Complete and save all verification checklist items before activating.";
        setError(msg);
        toast.error(msg);
        onExpand();
        return;
      }
      const msg = describeError(e);
      setError(msg);
      toast.error(msg);
    },
  });
  const suspend = useMutation({
    mutationFn: () => suspendDoctor(doctor.id),
    onSuccess: () => {
      toast.success(`${doctor.full_name} is suspended.`);
      invalidate();
    },
    onError: toastFailure,
  });
  const reactivate = useMutation({
    mutationFn: () => reactivateDoctor(doctor.id),
    onSuccess: () => {
      toast.success(`${doctor.full_name} is active again.`);
      invalidate();
    },
    onError: toastFailure,
  });
  // Turning login off signs the doctor out everywhere, so the switch asks first; on doesn't (ADM-04).
  const [askOff, setAskOff] = useState(false);
  const loginAccess = useMutation({
    mutationFn: (on: boolean) => setDoctorLoginAccess({ login_enabled: on, doctor_ids: [doctor.id] }),
    onSuccess: (res) => {
      toast.success(`Login turned ${res.login_enabled ? "on" : "off"} for ${doctor.full_name}.`);
      setAskOff(false);
      invalidate();
    },
    onError: toastFailure,
  });

  return (
    <div className={expanded ? "bg-gray-50" : selected ? "bg-brand/5" : "transition-colors hover:bg-gray-50"}>
      <TableRow
        columns={COLUMNS}
        className="min-h-15 py-2 text-sm"
        cells={{
          select: <SelectBox checked={selected} onChange={onSelect} label={`Select ${doctor.full_name}`} />,
          name: <DoctorNameCell doctor={doctor} />,
          council: <span className="block truncate text-gray-600">{doctor.state_medical_council}</span>,
          verified: <VerifiedBadge verified={!!doctor.verified_at} />,
          status: <StatusBadge status={doctor.status} />,
          login: (
            <LoginSwitch
              on={doctor.login_enabled}
              busy={loginAccess.isPending}
              label={`Login for ${doctor.full_name}`}
              onToggle={() => (doctor.login_enabled ? setAskOff(true) : loginAccess.mutate(true))}
            />
          ),
          actions: (
            <RowActions className="flex-wrap">
              <IconButton label="Edit doctor" tone="default" onClick={() => onEdit(doctor)}>
                <EditIcon />
              </IconButton>
              {/* A pending doctor can't sign in until verified, and activating sends a sign-up link. */}
              {!isPending && (
                <IconButton label="Reset password" tone="default" onClick={() => onResetPassword(doctor)}>
                  <KeyIcon />
                </IconButton>
              )}
              {canVerify && (
                <IconButton label={expanded ? "Hide checklist" : "Open checklist"} tone="default" onClick={onToggle}>
                  <ChecklistIcon />
                </IconButton>
              )}
              {canVerify && (
                <IconButton label="Verify & activate" tone="success" onClick={() => activate.mutate()}>
                  <CheckIcon />
                </IconButton>
              )}
              {doctor.status === "active" && (
                <IconButton label="Suspend" tone="danger" onClick={() => suspend.mutate()}>
                  <PauseIcon />
                </IconButton>
              )}
              {doctor.status === "suspended" && (
                <IconButton label="Reactivate" tone="success" onClick={() => reactivate.mutate()}>
                  <PlayIcon />
                </IconButton>
              )}
            </RowActions>
          ),
        }}
      />

      {expanded && canVerify && (
        <div className="border-t bg-white px-4 py-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {DOCTOR_CHECKLIST_ITEMS.map((item) => (
              <label key={item} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={checklist[item] ?? false}
                  onChange={(e) => {
                    setChecklist({ ...checklist, [item]: e.target.checked });
                    saveChecklist.reset();
                  }}
                />
                {item.replace(/_/g, " ")}
              </label>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <IconButton
              label={checklistSaved ? "Checklist saved" : "Save checklist"}
              tone={checklistSaved ? "success" : "brand"}
              disabled={saveChecklist.isPending}
              onClick={() => saveChecklist.mutate()}
            >
              {checklistSaved ? <CheckIcon /> : <SaveIcon />}
            </IconButton>
            <span className={`text-xs ${checklistSaved ? "font-medium text-success" : "text-gray-400"}`}>
              {saveChecklist.isPending ? "Saving…" : checklistSaved ? "Checklist saved" : "Save checklist"}
            </span>
            {error && <span className="text-sm text-red-600">{error}</span>}
          </div>
        </div>
      )}

      <ConfirmLogin
        on={askOff ? false : null}
        who={doctor.full_name}
        busy={loginAccess.isPending}
        onConfirm={(on) => loginAccess.mutate(on)}
        onClose={() => setAskOff(false)}
      />
    </div>
  );
}

/** Name and license; where the council, verified and status columns are left out, those too. */
function DoctorNameCell({ doctor }: { doctor: Doctor }) {
  return (
    <div className="min-w-0">
      <div className="truncate font-medium text-gray-800">{doctor.full_name}</div>
      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-gray-400">
        <span className="truncate">license {doctor.license_number_masked}</span>
        {doctor.added_by_role === "pharmacist" && (
          <span
            className="shrink-0 rounded-full bg-sky-50 px-1.5 py-px text-[11px] font-medium text-sky-700"
            title={doctor.added_by_name ? `Added by ${doctor.added_by_name} while digitising` : "Added while digitising"}
          >
            Added by pharmacy
          </span>
        )}
      </div>
      <div className="truncate text-xs text-gray-500 lg:hidden">{doctor.state_medical_council}</div>
      <div className="mt-1 flex flex-wrap gap-1.5 md:hidden">
        <span className="sm:hidden">
          <StatusBadge status={doctor.status} />
        </span>
        <VerifiedBadge verified={!!doctor.verified_at} long />
      </div>
    </div>
  );
}

// ---------------- bits ----------------

/** Asks before a login change: for the selected doctors, or for one doctor's login going off. */
function ConfirmLogin({
  on,
  who,
  notActive = 0,
  busy,
  onConfirm,
  onClose,
}: {
  on: boolean | null; // the login value to confirm; null = closed
  who: string;
  notActive?: number;
  busy: boolean;
  onConfirm: (on: boolean) => void;
  onClose: () => void;
}) {
  return (
    <Modal open={on !== null} title={on ? "Turn login on?" : "Turn login off?"} onClose={() => !busy && onClose()}>
      <div className="space-y-4 text-sm text-gray-600">
        {on ? (
          <p>
            <b>{who}</b> will be able to sign in at the doctor login page with their phone number — a one-time code
            first, then a password of their own. Nothing is sent to them.
          </p>
        ) : (
          <p>
            <b>{who}</b> will be signed out everywhere and can't sign in until login is turned back on. Prescriptions
            under their name aren't affected.
          </p>
        )}
        {on && notActive > 0 && (
          <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
            {notActive} of them {notActive === 1 ? "is" : "are"} pending or suspended, and can sign in only once active.
          </p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => on !== null && onConfirm(on)}
            disabled={busy}
            data-testid="confirm-login-change"
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${on ? "bg-emerald-600" : "bg-red-600"}`}
          >
            {busy ? "Saving…" : on ? "Turn login on" : "Turn login off"}
          </button>
          <button type="button" onClick={onClose} disabled={busy} className="rounded-lg border px-4 py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SelectBox({
  checked,
  indeterminate = false,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
      title={label}
      className="h-4 w-4 cursor-pointer accent-brand disabled:cursor-default"
    />
  );
}

function LoginSwitch({ on, busy, label, onToggle }: { on: boolean; busy: boolean; label: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={on ? "Login on — click to turn off" : "Login off — click to turn on"}
      disabled={busy}
      onClick={onToggle}
      className="flex w-fit items-center gap-2 disabled:opacity-50"
    >
      <span className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-gray-300"}`}>
        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
      <span className={`text-xs ${on ? "text-emerald-700" : "text-gray-500"}`}>{on ? "On" : "Off"}</span>
    </button>
  );
}

/** "No" under the Verified header; spelt out (`long`) where it stands on its own. */
function VerifiedBadge({ verified, long = false }: { verified: boolean; long?: boolean }) {
  return verified ? <Badge tone="success">Verified</Badge> : <Badge tone="muted">{long ? "Not verified" : "No"}</Badge>;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, "success" | "warning" | "danger" | "muted"> = {
    active: "success",
    pending_verification: "warning",
    suspended: "danger",
  };
  const label = status === "pending_verification" ? "Pending" : status[0].toUpperCase() + status.slice(1);
  return <Badge tone={map[status] ?? "muted"}>{label}</Badge>;
}

function Badge({ tone, children }: { tone: "success" | "warning" | "danger" | "muted"; children: React.ReactNode }) {
  const cls = {
    success: "bg-emerald-50 text-emerald-700",
    warning: "bg-amber-50 text-amber-700",
    danger: "bg-red-50 text-red-700",
    muted: "bg-gray-100 text-gray-500",
  }[tone];
  return <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

function Control({ label, children, className = "" }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`flex flex-col gap-1 text-xs text-gray-500 ${className}`}>
      {label}
      {children}
    </label>
  );
}

/**
 * Admin-only edit of a doctor's editable fields (PATCH /admin/doctors/{id}). Phone + license are
 * immutable and not shown; verification/status changes happen via the row actions. An emptied
 * email or specialisation is sent as null, which clears it (ADM-12).
 */
function EditDoctorForm({
  doctor,
  onClose,
  onSaved,
}: {
  doctor: Doctor;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    full_name: doctor.full_name,
    email: doctor.email ?? "",
    state_medical_council: doctor.state_medical_council,
    specialisation: doctor.specialisation ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      updateDoctor(doctor.id, {
        full_name: form.full_name,
        email: form.email.trim() || null,
        state_medical_council: form.state_medical_council,
        specialisation: form.specialisation.trim() || null,
      }),
    onSuccess: onSaved,
    onError: (e) => setError(describeError(e)),
  });

  const set = (p: Partial<typeof form>) => setForm({ ...form, ...p });
  const input = "w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm outline-hidden focus:border-brand focus:ring-1 focus:ring-brand";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
      className="space-y-3"
    >
      <p className="text-xs text-gray-400">Phone and license number can’t be changed here.</p>
      <PrefixInput prefix="Dr. " label="Full name" value={form.full_name} onChange={(v) => set({ full_name: v })} required />
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">Phone</span>
        <input
          value={doctor.phone ?? "—"}
          readOnly
          disabled
          className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-500"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">Email</span>
        <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className={input} />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">State medical council</span>
        <input value={form.state_medical_council} onChange={(e) => set({ state_medical_council: e.target.value })} required className={input} />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">Specialisation</span>
        <input value={form.specialisation} onChange={(e) => set({ specialisation: e.target.value })} className={input} />
      </label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2 pt-1">
        <button disabled={save.isPending} className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50">
          {save.isPending ? "Saving…" : "Save changes"}
        </button>
        <button type="button" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}
